/**
 * Canonical country reference data: ISO-3166-1 alpha-2 code, English short-form
 * name, E.164 dial code and emoji flag. Sorted by name.
 *
 * This is the single source of truth for countries in the app - add or correct
 * a country here and every consumer picks it up.
 *
 * Two things to know before editing:
 *
 * - Dial codes are plain `+` followed by digits, never hyphenated. A hyphenated
 *   code concatenated into a phone number embeds the hyphen in the stored value.
 * - Dial codes are NOT unique. `+44` covers four entries, `+1` two, and `+61`
 *   three. Anything that needs to identify a country must key on `iso2`.
 */
export const COUNTRIES = [
  { iso2: 'AF', name: 'Afghanistan', dial: '+93', flag: '🇦🇫' },
  { iso2: 'AX', name: 'Åland Islands', dial: '+358', flag: '🇦🇽' },
  { iso2: 'AL', name: 'Albania', dial: '+355', flag: '🇦🇱' },
  { iso2: 'DZ', name: 'Algeria', dial: '+213', flag: '🇩🇿' },
  { iso2: 'AS', name: 'American Samoa', dial: '+1684', flag: '🇦🇸' },
  { iso2: 'AD', name: 'Andorra', dial: '+376', flag: '🇦🇩' },
  { iso2: 'AO', name: 'Angola', dial: '+244', flag: '🇦🇴' },
  { iso2: 'AI', name: 'Anguilla', dial: '+1264', flag: '🇦🇮' },
  { iso2: 'AQ', name: 'Antarctica', dial: '+672', flag: '🇦🇶' },
  { iso2: 'AG', name: 'Antigua and Barbuda', dial: '+1268', flag: '🇦🇬' },
  { iso2: 'AR', name: 'Argentina', dial: '+54', flag: '🇦🇷' },
  { iso2: 'AM', name: 'Armenia', dial: '+374', flag: '🇦🇲' },
  { iso2: 'AW', name: 'Aruba', dial: '+297', flag: '🇦🇼' },
  { iso2: 'AU', name: 'Australia', dial: '+61', flag: '🇦🇺' },
  { iso2: 'AT', name: 'Austria', dial: '+43', flag: '🇦🇹' },
  { iso2: 'AZ', name: 'Azerbaijan', dial: '+994', flag: '🇦🇿' },
  { iso2: 'BS', name: 'Bahamas', dial: '+1242', flag: '🇧🇸' },
  { iso2: 'BH', name: 'Bahrain', dial: '+973', flag: '🇧🇭' },
  { iso2: 'BD', name: 'Bangladesh', dial: '+880', flag: '🇧🇩' },
  { iso2: 'BB', name: 'Barbados', dial: '+1246', flag: '🇧🇧' },
  { iso2: 'BY', name: 'Belarus', dial: '+375', flag: '🇧🇾' },
  { iso2: 'BE', name: 'Belgium', dial: '+32', flag: '🇧🇪' },
  { iso2: 'BZ', name: 'Belize', dial: '+501', flag: '🇧🇿' },
  { iso2: 'BJ', name: 'Benin', dial: '+229', flag: '🇧🇯' },
  { iso2: 'BM', name: 'Bermuda', dial: '+1441', flag: '🇧🇲' },
  { iso2: 'BT', name: 'Bhutan', dial: '+975', flag: '🇧🇹' },
  { iso2: 'BO', name: 'Bolivia', dial: '+591', flag: '🇧🇴' },
  { iso2: 'BA', name: 'Bosnia and Herzegovina', dial: '+387', flag: '🇧🇦' },
  { iso2: 'BW', name: 'Botswana', dial: '+267', flag: '🇧🇼' },
  { iso2: 'BR', name: 'Brazil', dial: '+55', flag: '🇧🇷' },
  {
    iso2: 'IO',
    name: 'British Indian Ocean Territory',
    dial: '+246',
    flag: '🇮🇴',
  },
  { iso2: 'VG', name: 'British Virgin Islands', dial: '+1284', flag: '🇻🇬' },
  { iso2: 'BN', name: 'Brunei', dial: '+673', flag: '🇧🇳' },
  { iso2: 'BG', name: 'Bulgaria', dial: '+359', flag: '🇧🇬' },
  { iso2: 'BF', name: 'Burkina Faso', dial: '+226', flag: '🇧🇫' },
  { iso2: 'BI', name: 'Burundi', dial: '+257', flag: '🇧🇮' },
  { iso2: 'KH', name: 'Cambodia', dial: '+855', flag: '🇰🇭' },
  { iso2: 'CM', name: 'Cameroon', dial: '+237', flag: '🇨🇲' },
  { iso2: 'CA', name: 'Canada', dial: '+1', flag: '🇨🇦' },
  { iso2: 'CV', name: 'Cape Verde', dial: '+238', flag: '🇨🇻' },
  { iso2: 'BQ', name: 'Caribbean Netherlands', dial: '+599', flag: '🇧🇶' },
  { iso2: 'KY', name: 'Cayman Islands', dial: '+1345', flag: '🇰🇾' },
  { iso2: 'CF', name: 'Central African Republic', dial: '+236', flag: '🇨🇫' },
  { iso2: 'TD', name: 'Chad', dial: '+235', flag: '🇹🇩' },
  { iso2: 'CL', name: 'Chile', dial: '+56', flag: '🇨🇱' },
  { iso2: 'CN', name: 'China', dial: '+86', flag: '🇨🇳' },
  { iso2: 'CX', name: 'Christmas Island', dial: '+61', flag: '🇨🇽' },
  { iso2: 'CC', name: 'Cocos (Keeling) Islands', dial: '+61', flag: '🇨🇨' },
  { iso2: 'CO', name: 'Colombia', dial: '+57', flag: '🇨🇴' },
  { iso2: 'KM', name: 'Comoros', dial: '+269', flag: '🇰🇲' },
  { iso2: 'CK', name: 'Cook Islands', dial: '+682', flag: '🇨🇰' },
  { iso2: 'CR', name: 'Costa Rica', dial: '+506', flag: '🇨🇷' },
  { iso2: 'CI', name: "Côte d'Ivoire", dial: '+225', flag: '🇨🇮' },
  { iso2: 'HR', name: 'Croatia', dial: '+385', flag: '🇭🇷' },
  { iso2: 'CU', name: 'Cuba', dial: '+53', flag: '🇨🇺' },
  { iso2: 'CW', name: 'Curaçao', dial: '+599', flag: '🇨🇼' },
  { iso2: 'CY', name: 'Cyprus', dial: '+357', flag: '🇨🇾' },
  { iso2: 'CZ', name: 'Czechia', dial: '+420', flag: '🇨🇿' },
  { iso2: 'DK', name: 'Denmark', dial: '+45', flag: '🇩🇰' },
  { iso2: 'DJ', name: 'Djibouti', dial: '+253', flag: '🇩🇯' },
  { iso2: 'DM', name: 'Dominica', dial: '+1767', flag: '🇩🇲' },
  { iso2: 'DO', name: 'Dominican Republic', dial: '+1809', flag: '🇩🇴' },
  { iso2: 'CD', name: 'DR Congo', dial: '+243', flag: '🇨🇩' },
  { iso2: 'EC', name: 'Ecuador', dial: '+593', flag: '🇪🇨' },
  { iso2: 'EG', name: 'Egypt', dial: '+20', flag: '🇪🇬' },
  { iso2: 'SV', name: 'El Salvador', dial: '+503', flag: '🇸🇻' },
  { iso2: 'GQ', name: 'Equatorial Guinea', dial: '+240', flag: '🇬🇶' },
  { iso2: 'ER', name: 'Eritrea', dial: '+291', flag: '🇪🇷' },
  { iso2: 'EE', name: 'Estonia', dial: '+372', flag: '🇪🇪' },
  { iso2: 'SZ', name: 'Eswatini', dial: '+268', flag: '🇸🇿' },
  { iso2: 'ET', name: 'Ethiopia', dial: '+251', flag: '🇪🇹' },
  { iso2: 'FK', name: 'Falkland Islands', dial: '+500', flag: '🇫🇰' },
  { iso2: 'FO', name: 'Faroe Islands', dial: '+298', flag: '🇫🇴' },
  { iso2: 'FJ', name: 'Fiji', dial: '+679', flag: '🇫🇯' },
  { iso2: 'FI', name: 'Finland', dial: '+358', flag: '🇫🇮' },
  { iso2: 'FR', name: 'France', dial: '+33', flag: '🇫🇷' },
  { iso2: 'GF', name: 'French Guiana', dial: '+594', flag: '🇬🇫' },
  { iso2: 'PF', name: 'French Polynesia', dial: '+689', flag: '🇵🇫' },
  { iso2: 'TF', name: 'French Southern Territories', dial: '+262', flag: '🇹🇫' },
  { iso2: 'GA', name: 'Gabon', dial: '+241', flag: '🇬🇦' },
  { iso2: 'GM', name: 'Gambia', dial: '+220', flag: '🇬🇲' },
  { iso2: 'GE', name: 'Georgia', dial: '+995', flag: '🇬🇪' },
  { iso2: 'DE', name: 'Germany', dial: '+49', flag: '🇩🇪' },
  { iso2: 'GH', name: 'Ghana', dial: '+233', flag: '🇬🇭' },
  { iso2: 'GI', name: 'Gibraltar', dial: '+350', flag: '🇬🇮' },
  { iso2: 'GR', name: 'Greece', dial: '+30', flag: '🇬🇷' },
  { iso2: 'GL', name: 'Greenland', dial: '+299', flag: '🇬🇱' },
  { iso2: 'GD', name: 'Grenada', dial: '+1473', flag: '🇬🇩' },
  { iso2: 'GP', name: 'Guadeloupe', dial: '+590', flag: '🇬🇵' },
  { iso2: 'GU', name: 'Guam', dial: '+1671', flag: '🇬🇺' },
  { iso2: 'GT', name: 'Guatemala', dial: '+502', flag: '🇬🇹' },
  { iso2: 'GG', name: 'Guernsey', dial: '+44', flag: '🇬🇬' },
  { iso2: 'GN', name: 'Guinea', dial: '+224', flag: '🇬🇳' },
  { iso2: 'GW', name: 'Guinea-Bissau', dial: '+245', flag: '🇬🇼' },
  { iso2: 'GY', name: 'Guyana', dial: '+592', flag: '🇬🇾' },
  { iso2: 'HT', name: 'Haiti', dial: '+509', flag: '🇭🇹' },
  { iso2: 'HN', name: 'Honduras', dial: '+504', flag: '🇭🇳' },
  { iso2: 'HK', name: 'Hong Kong', dial: '+852', flag: '🇭🇰' },
  { iso2: 'HU', name: 'Hungary', dial: '+36', flag: '🇭🇺' },
  { iso2: 'IS', name: 'Iceland', dial: '+354', flag: '🇮🇸' },
  { iso2: 'IN', name: 'India', dial: '+91', flag: '🇮🇳' },
  { iso2: 'ID', name: 'Indonesia', dial: '+62', flag: '🇮🇩' },
  { iso2: 'IR', name: 'Iran', dial: '+98', flag: '🇮🇷' },
  { iso2: 'IQ', name: 'Iraq', dial: '+964', flag: '🇮🇶' },
  { iso2: 'IE', name: 'Ireland', dial: '+353', flag: '🇮🇪' },
  { iso2: 'IM', name: 'Isle of Man', dial: '+44', flag: '🇮🇲' },
  { iso2: 'IL', name: 'Israel', dial: '+972', flag: '🇮🇱' },
  { iso2: 'IT', name: 'Italy', dial: '+39', flag: '🇮🇹' },
  { iso2: 'JM', name: 'Jamaica', dial: '+1876', flag: '🇯🇲' },
  { iso2: 'JP', name: 'Japan', dial: '+81', flag: '🇯🇵' },
  { iso2: 'JE', name: 'Jersey', dial: '+44', flag: '🇯🇪' },
  { iso2: 'JO', name: 'Jordan', dial: '+962', flag: '🇯🇴' },
  { iso2: 'KZ', name: 'Kazakhstan', dial: '+7', flag: '🇰🇿' },
  { iso2: 'KE', name: 'Kenya', dial: '+254', flag: '🇰🇪' },
  { iso2: 'KI', name: 'Kiribati', dial: '+686', flag: '🇰🇮' },
  { iso2: 'XK', name: 'Kosovo', dial: '+383', flag: '🇽🇰' },
  { iso2: 'KW', name: 'Kuwait', dial: '+965', flag: '🇰🇼' },
  { iso2: 'KG', name: 'Kyrgyzstan', dial: '+996', flag: '🇰🇬' },
  { iso2: 'LA', name: 'Laos', dial: '+856', flag: '🇱🇦' },
  { iso2: 'LV', name: 'Latvia', dial: '+371', flag: '🇱🇻' },
  { iso2: 'LB', name: 'Lebanon', dial: '+961', flag: '🇱🇧' },
  { iso2: 'LS', name: 'Lesotho', dial: '+266', flag: '🇱🇸' },
  { iso2: 'LR', name: 'Liberia', dial: '+231', flag: '🇱🇷' },
  { iso2: 'LY', name: 'Libya', dial: '+218', flag: '🇱🇾' },
  { iso2: 'LI', name: 'Liechtenstein', dial: '+423', flag: '🇱🇮' },
  { iso2: 'LT', name: 'Lithuania', dial: '+370', flag: '🇱🇹' },
  { iso2: 'LU', name: 'Luxembourg', dial: '+352', flag: '🇱🇺' },
  { iso2: 'MO', name: 'Macau', dial: '+853', flag: '🇲🇴' },
  { iso2: 'MG', name: 'Madagascar', dial: '+261', flag: '🇲🇬' },
  { iso2: 'MW', name: 'Malawi', dial: '+265', flag: '🇲🇼' },
  { iso2: 'MY', name: 'Malaysia', dial: '+60', flag: '🇲🇾' },
  { iso2: 'MV', name: 'Maldives', dial: '+960', flag: '🇲🇻' },
  { iso2: 'ML', name: 'Mali', dial: '+223', flag: '🇲🇱' },
  { iso2: 'MT', name: 'Malta', dial: '+356', flag: '🇲🇹' },
  { iso2: 'MH', name: 'Marshall Islands', dial: '+692', flag: '🇲🇭' },
  { iso2: 'MQ', name: 'Martinique', dial: '+596', flag: '🇲🇶' },
  { iso2: 'MR', name: 'Mauritania', dial: '+222', flag: '🇲🇷' },
  { iso2: 'MU', name: 'Mauritius', dial: '+230', flag: '🇲🇺' },
  { iso2: 'YT', name: 'Mayotte', dial: '+262', flag: '🇾🇹' },
  { iso2: 'MX', name: 'Mexico', dial: '+52', flag: '🇲🇽' },
  { iso2: 'FM', name: 'Micronesia', dial: '+691', flag: '🇫🇲' },
  { iso2: 'MD', name: 'Moldova', dial: '+373', flag: '🇲🇩' },
  { iso2: 'MC', name: 'Monaco', dial: '+377', flag: '🇲🇨' },
  { iso2: 'MN', name: 'Mongolia', dial: '+976', flag: '🇲🇳' },
  { iso2: 'ME', name: 'Montenegro', dial: '+382', flag: '🇲🇪' },
  { iso2: 'MS', name: 'Montserrat', dial: '+1664', flag: '🇲🇸' },
  { iso2: 'MA', name: 'Morocco', dial: '+212', flag: '🇲🇦' },
  { iso2: 'MZ', name: 'Mozambique', dial: '+258', flag: '🇲🇿' },
  { iso2: 'MM', name: 'Myanmar', dial: '+95', flag: '🇲🇲' },
  { iso2: 'NA', name: 'Namibia', dial: '+264', flag: '🇳🇦' },
  { iso2: 'NR', name: 'Nauru', dial: '+674', flag: '🇳🇷' },
  { iso2: 'NP', name: 'Nepal', dial: '+977', flag: '🇳🇵' },
  { iso2: 'NL', name: 'Netherlands', dial: '+31', flag: '🇳🇱' },
  { iso2: 'NC', name: 'New Caledonia', dial: '+687', flag: '🇳🇨' },
  { iso2: 'NZ', name: 'New Zealand', dial: '+64', flag: '🇳🇿' },
  { iso2: 'NI', name: 'Nicaragua', dial: '+505', flag: '🇳🇮' },
  { iso2: 'NE', name: 'Niger', dial: '+227', flag: '🇳🇪' },
  { iso2: 'NG', name: 'Nigeria', dial: '+234', flag: '🇳🇬' },
  { iso2: 'NU', name: 'Niue', dial: '+683', flag: '🇳🇺' },
  { iso2: 'NF', name: 'Norfolk Island', dial: '+672', flag: '🇳🇫' },
  { iso2: 'KP', name: 'North Korea', dial: '+850', flag: '🇰🇵' },
  { iso2: 'MK', name: 'North Macedonia', dial: '+389', flag: '🇲🇰' },
  { iso2: 'MP', name: 'Northern Mariana Islands', dial: '+1670', flag: '🇲🇵' },
  { iso2: 'NO', name: 'Norway', dial: '+47', flag: '🇳🇴' },
  { iso2: 'OM', name: 'Oman', dial: '+968', flag: '🇴🇲' },
  { iso2: 'PK', name: 'Pakistan', dial: '+92', flag: '🇵🇰' },
  { iso2: 'PW', name: 'Palau', dial: '+680', flag: '🇵🇼' },
  { iso2: 'PS', name: 'Palestine', dial: '+970', flag: '🇵🇸' },
  { iso2: 'PA', name: 'Panama', dial: '+507', flag: '🇵🇦' },
  { iso2: 'PG', name: 'Papua New Guinea', dial: '+675', flag: '🇵🇬' },
  { iso2: 'PY', name: 'Paraguay', dial: '+595', flag: '🇵🇾' },
  { iso2: 'PE', name: 'Peru', dial: '+51', flag: '🇵🇪' },
  { iso2: 'PH', name: 'Philippines', dial: '+63', flag: '🇵🇭' },
  { iso2: 'PN', name: 'Pitcairn Islands', dial: '+64', flag: '🇵🇳' },
  { iso2: 'PL', name: 'Poland', dial: '+48', flag: '🇵🇱' },
  { iso2: 'PT', name: 'Portugal', dial: '+351', flag: '🇵🇹' },
  { iso2: 'PR', name: 'Puerto Rico', dial: '+1787', flag: '🇵🇷' },
  { iso2: 'QA', name: 'Qatar', dial: '+974', flag: '🇶🇦' },
  { iso2: 'CG', name: 'Republic of the Congo', dial: '+242', flag: '🇨🇬' },
  { iso2: 'RE', name: 'Réunion', dial: '+262', flag: '🇷🇪' },
  { iso2: 'RO', name: 'Romania', dial: '+40', flag: '🇷🇴' },
  { iso2: 'RU', name: 'Russia', dial: '+7', flag: '🇷🇺' },
  { iso2: 'RW', name: 'Rwanda', dial: '+250', flag: '🇷🇼' },
  { iso2: 'BL', name: 'Saint Barthélemy', dial: '+590', flag: '🇧🇱' },
  { iso2: 'SH', name: 'Saint Helena', dial: '+290', flag: '🇸🇭' },
  { iso2: 'KN', name: 'Saint Kitts and Nevis', dial: '+1869', flag: '🇰🇳' },
  { iso2: 'LC', name: 'Saint Lucia', dial: '+1758', flag: '🇱🇨' },
  { iso2: 'MF', name: 'Saint Martin', dial: '+590', flag: '🇲🇫' },
  { iso2: 'PM', name: 'Saint Pierre and Miquelon', dial: '+508', flag: '🇵🇲' },
  {
    iso2: 'VC',
    name: 'Saint Vincent and Grenadines',
    dial: '+1784',
    flag: '🇻🇨',
  },
  { iso2: 'WS', name: 'Samoa', dial: '+685', flag: '🇼🇸' },
  { iso2: 'SM', name: 'San Marino', dial: '+378', flag: '🇸🇲' },
  { iso2: 'ST', name: 'São Tomé and Príncipe', dial: '+239', flag: '🇸🇹' },
  { iso2: 'SA', name: 'Saudi Arabia', dial: '+966', flag: '🇸🇦' },
  { iso2: 'SN', name: 'Senegal', dial: '+221', flag: '🇸🇳' },
  { iso2: 'RS', name: 'Serbia', dial: '+381', flag: '🇷🇸' },
  { iso2: 'SC', name: 'Seychelles', dial: '+248', flag: '🇸🇨' },
  { iso2: 'SL', name: 'Sierra Leone', dial: '+232', flag: '🇸🇱' },
  { iso2: 'SG', name: 'Singapore', dial: '+65', flag: '🇸🇬' },
  { iso2: 'SX', name: 'Sint Maarten', dial: '+1721', flag: '🇸🇽' },
  { iso2: 'SK', name: 'Slovakia', dial: '+421', flag: '🇸🇰' },
  { iso2: 'SI', name: 'Slovenia', dial: '+386', flag: '🇸🇮' },
  { iso2: 'SB', name: 'Solomon Islands', dial: '+677', flag: '🇸🇧' },
  { iso2: 'SO', name: 'Somalia', dial: '+252', flag: '🇸🇴' },
  { iso2: 'ZA', name: 'South Africa', dial: '+27', flag: '🇿🇦' },
  {
    iso2: 'GS',
    name: 'South Georgia and the South Sandwich Islands',
    dial: '+500',
    flag: '🇬🇸',
  },
  { iso2: 'KR', name: 'South Korea', dial: '+82', flag: '🇰🇷' },
  { iso2: 'SS', name: 'South Sudan', dial: '+211', flag: '🇸🇸' },
  { iso2: 'ES', name: 'Spain', dial: '+34', flag: '🇪🇸' },
  { iso2: 'LK', name: 'Sri Lanka', dial: '+94', flag: '🇱🇰' },
  { iso2: 'SD', name: 'Sudan', dial: '+249', flag: '🇸🇩' },
  { iso2: 'SR', name: 'Suriname', dial: '+597', flag: '🇸🇷' },
  { iso2: 'SJ', name: 'Svalbard and Jan Mayen', dial: '+47', flag: '🇸🇯' },
  { iso2: 'SE', name: 'Sweden', dial: '+46', flag: '🇸🇪' },
  { iso2: 'CH', name: 'Switzerland', dial: '+41', flag: '🇨🇭' },
  { iso2: 'SY', name: 'Syria', dial: '+963', flag: '🇸🇾' },
  { iso2: 'TW', name: 'Taiwan', dial: '+886', flag: '🇹🇼' },
  { iso2: 'TJ', name: 'Tajikistan', dial: '+992', flag: '🇹🇯' },
  { iso2: 'TZ', name: 'Tanzania', dial: '+255', flag: '🇹🇿' },
  { iso2: 'TH', name: 'Thailand', dial: '+66', flag: '🇹🇭' },
  { iso2: 'TL', name: 'Timor-Leste', dial: '+670', flag: '🇹🇱' },
  { iso2: 'TG', name: 'Togo', dial: '+228', flag: '🇹🇬' },
  { iso2: 'TK', name: 'Tokelau', dial: '+690', flag: '🇹🇰' },
  { iso2: 'TO', name: 'Tonga', dial: '+676', flag: '🇹🇴' },
  { iso2: 'TT', name: 'Trinidad and Tobago', dial: '+1868', flag: '🇹🇹' },
  { iso2: 'TN', name: 'Tunisia', dial: '+216', flag: '🇹🇳' },
  { iso2: 'TR', name: 'Turkey', dial: '+90', flag: '🇹🇷' },
  { iso2: 'TM', name: 'Turkmenistan', dial: '+993', flag: '🇹🇲' },
  { iso2: 'TC', name: 'Turks and Caicos Islands', dial: '+1649', flag: '🇹🇨' },
  { iso2: 'TV', name: 'Tuvalu', dial: '+688', flag: '🇹🇻' },
  { iso2: 'VI', name: 'U.S. Virgin Islands', dial: '+1340', flag: '🇻🇮' },
  { iso2: 'UG', name: 'Uganda', dial: '+256', flag: '🇺🇬' },
  { iso2: 'UA', name: 'Ukraine', dial: '+380', flag: '🇺🇦' },
  { iso2: 'AE', name: 'United Arab Emirates', dial: '+971', flag: '🇦🇪' },
  { iso2: 'GB', name: 'United Kingdom', dial: '+44', flag: '🇬🇧' },
  { iso2: 'US', name: 'United States', dial: '+1', flag: '🇺🇸' },
  { iso2: 'UY', name: 'Uruguay', dial: '+598', flag: '🇺🇾' },
  { iso2: 'UZ', name: 'Uzbekistan', dial: '+998', flag: '🇺🇿' },
  { iso2: 'VU', name: 'Vanuatu', dial: '+678', flag: '🇻🇺' },
  { iso2: 'VA', name: 'Vatican City', dial: '+39', flag: '🇻🇦' },
  { iso2: 'VE', name: 'Venezuela', dial: '+58', flag: '🇻🇪' },
  { iso2: 'VN', name: 'Vietnam', dial: '+84', flag: '🇻🇳' },
  { iso2: 'WF', name: 'Wallis and Futuna', dial: '+681', flag: '🇼🇫' },
  { iso2: 'EH', name: 'Western Sahara', dial: '+212', flag: '🇪🇭' },
  { iso2: 'YE', name: 'Yemen', dial: '+967', flag: '🇾🇪' },
  { iso2: 'ZM', name: 'Zambia', dial: '+260', flag: '🇿🇲' },
  { iso2: 'ZW', name: 'Zimbabwe', dial: '+263', flag: '🇿🇼' },
];

/** Look up a single country by its ISO-3166-1 alpha-2 code. */
export function findCountry(iso2) {
  if (!iso2) return null;
  const code = String(iso2).toUpperCase();
  return COUNTRIES.find((c) => c.iso2 === code) || null;
}
